// Phase 3 — central state for the Decision Engine page.
//
// Step 1 GOAL: centralize state only. The page still owns all behaviour and
// effects; this module simply provides a single `useReducer` + Context so
// future child components (Step 2) can read/write state without prop
// drilling.
//
// REDUCER PATTERN — patch-style, intentionally tiny:
//   • Dispatch a plain object → shallow-merged into state.
//       dispatch({ input: '' })
//       dispatch({ loading: true, showAutocomplete: false })
//   • Dispatch a function → called with current state, returns a patch.
//       dispatch((s) => ({ history: [...s.history, turn] }))
//       dispatch((s) => ({ recentRefreshKey: s.recentRefreshKey + 1 }))
//
// This keeps call-sites byte-equivalent to the previous useState code
// (`setInput(v)` → `dispatch({ input: v })`) and avoids the boilerplate of
// enumerating every action type.
import React, { createContext, useCallback, useContext, useReducer, useRef } from 'react';

// ---------------------------------------------------------------------------
// Session id helpers — preserved from the original page implementation.
// SESSION_KEY value is identical to the one originally hard-coded in
// DecisionEnginePage.js so existing per-tab session ids carry over without
// invalidation after this refactor lands.
// ---------------------------------------------------------------------------
const SESSION_KEY = 'pfap.intel.sessionId.v1';

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `intel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

export { SESSION_KEY, getSessionId };

// ---------------------------------------------------------------------------
// Initial state + reducer.
// ---------------------------------------------------------------------------
export const buildInitialState = () => ({
  // Input bar
  input: '',
  // Conversation feed
  history: [], // [{id, query, response | error, loading, ts, context?, risk_snapshot?, risk_evolution?}]
  loading: false,
  // Slash-command autocomplete
  commands: [],
  showAutocomplete: false,
  autocompleteIdx: 0,
  // Session
  sessionId: getSessionId(),
  recentRefreshKey: 0,
  // @-mention player picker
  athletes: [],
  athletesLoaded: false,
  selectedPlayers: [], // [{id, full_name}]
  showPlayerPicker: false,
  playerPickerIdx: 0,
  // Sticky-context toggle
  stickyContext: false,
});

export function decisionEngineReducer(state, action) {
  // Function-style dispatch → call with state to derive the patch.
  const patch = typeof action === 'function' ? action(state) : action;
  if (!patch || typeof patch !== 'object') return state;
  return { ...state, ...patch };
}

// ---------------------------------------------------------------------------
// Context + Provider + Hook
// ---------------------------------------------------------------------------
const DecisionEngineContext = createContext(null);

export const DecisionEngineProvider = ({ children }) => {
  const [state, dispatch] = useReducer(decisionEngineReducer, undefined, buildInitialState);
  // The feed `<div>` ref is fully encapsulated — consumers register the
  // element via `setFeedElement` (callback ref) and trigger scrolling via
  // `scrollToBottom()`. The raw ref is no longer part of the public shape
  // (Phase 3 Step 2d completion). `inputRef` stays public because both
  // KeyboardShortcuts and InputBar need to call `.focus()` imperatively.
  const feedRef = useRef(null);
  const inputRef = useRef(null);

  const setFeedElement = useCallback((el) => {
    feedRef.current = el;
  }, []);

  // Scroll the message feed to its bottom. No-op when the ref isn't
  // mounted yet (e.g. during the very first render). Stable identity via
  // useCallback so consumers can safely include it in effect deps.
  const scrollToBottom = useCallback(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const value = {
    state,
    dispatch,
    refs: { inputRef },
    setFeedElement,
    scrollToBottom,
  };
  return (
    <DecisionEngineContext.Provider value={value}>
      {children}
    </DecisionEngineContext.Provider>
  );
};

export const useDecisionEngine = () => {
  const ctx = useContext(DecisionEngineContext);
  if (!ctx) {
    throw new Error('useDecisionEngine must be used inside <DecisionEngineProvider>');
  }
  return ctx;
};
